import { addLog } from 'assets/components/feedback/Feedback';
import ModalForm from 'assets/components/form/modalForm/ModalForm';
import ThemeModalFormDefault from 'assets/components/form/modalForm/themes/Theme.ModalForm.Default';
import useApi from 'assets/hooks/api/useApi';
import { getLocales } from 'assets/locales/Locale';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import { combineStrings } from 'assets/utils/data/String';
import { HttpStatus } from 'config/Api.Config';
import { compact, set } from 'lodash';
import Dealer from 'models/dealerManagement/dealer/Model.Dealer';
import dealerSaveApi from 'models/dealerManagement/dealer/save/Api.Dealer.Save';
import dealerGroupDeleteProductDocumentApi from 'models/dealerManagement/dealerGroup/deleteProductDocument/Api.DealerGroup.DeleteProductDocument';
import dealerGroupDeleteProductEnhancementDocumentApi from 'models/dealerManagement/dealerGroup/deleteProductEnhancementDocument/Api.DealerGroup.DeleteProductEnhancementDocument';
import dealerGroupDeleteUiFileApi from 'models/dealerManagement/dealerGroup/deleteUiFile/Api.DealerGroup.DeleteUiFile';
import dealerGroupSaveApi from 'models/dealerManagement/dealerGroup/save/Api.DealerGroup.Save';
import dealerGroupUploadProductDocumentApi from 'models/dealerManagement/dealerGroup/uploadProductDocument/Api.DealerGroup.UploadProductDocument';
import dealerGroupUploadProductEnhancementDocumentApi from 'models/dealerManagement/dealerGroup/uploadProductEnhancementDocument/Api.DealerGroup.UploadProductEnhancementDocument';
import dealerGroupUploadUiFileApi from 'models/dealerManagement/dealerGroup/uploadUiFile/Api.DealerGroup.UploadUiFile';
import {
  documentTranslationDeleteApi,
  documentTranslationListApi,
  documentTranslationSaveApi,
} from 'models/localization/documentTranslation/Model.DocumentTranslation';
import { useMemo } from 'react';
import { useDealerGroupProvider } from '../DealerGroup.Hooks';
import DealerGroupClaimTypeAutomationRules from './edit/DealerGroup.ClaimTypeAutomationRules';
import DealerGroupContractConsentProductAggregate from './edit/DealerGroup.ContractConsentProductAggregate';
import DealerGroupGeneralForm from './edit/DealerGroup.General';
import DealerGroupProductDocumentForm from './edit/DealerGroup.ProductDocumentForm';
import DealerGroupProductEnhancementDocumentForm from './edit/DealerGroup.ProductEnhancementDocumentForm';
import DealerGroupUiSettingsForm from './edit/DealerGroup.UiSettingsForm';

export type DealerGroupForm = Omit<
  Api.Core.DealerGroup.Save.IRequest,
  'productDealerGroupDocuments' | 'productEnhancementDealerGroupDocuments' | 'claimTypeDealerGroupAutomationRules'
> & {
  logo: Hooks.Input.File.Info;
  productDealerGroupDocuments: Array<
    Utils.Unpacked<Api.Core.DealerGroup.Save.IRequest['productDealerGroupDocuments']> & {
      formFile?: Hooks.Input.File.Info;
      isDeleted?: boolean;
      translatedDocuments: Array<
        Api.Core.DocumentTranslation.Save.IRequest & {
          isDeleted?: boolean;
        }
      >;
    }
  >;
  productEnhancementDealerGroupDocuments: Array<
    Utils.Unpacked<Api.Core.DealerGroup.Save.IRequest['productEnhancementDealerGroupDocuments']> & {
      formFile?: Hooks.Input.File.Info;
      isDeleted?: boolean;
      translatedDocuments: Array<
        Api.Core.DocumentTranslation.Save.IRequest & {
          isDeleted?: boolean;
        }
      >;
    }
  >;
  claimTypeDealerGroupAutomationRules: Array<
    Utils.Unpacked<Api.Core.DealerGroup.Save.IRequest['claimTypeDealerGroupAutomationRules']> & {
      isDeleted?: boolean;
      isUpdated?: boolean;
      claimTypeId?: number;
    }
  >;
} & {
  newDistributorProducts: Model.IProduct[];
};
export default function DealerGroupEdit() {
  const { lang } = getLocales();
  const { user, isDistributor, isDealerOrDistributor, isAdministrator } = useAuthStore();
  const { selected, pageRouter, listApi, saveDealerGroupAutomationRules, saveDealerGroupClaimTypes } =
    useDealerGroupProvider();
  const { payload: dealersPayload } = useApi({ action: Dealer.list });

  const productDealerGroupDocIds = useMemo(
    () => compact(selected?.productDealerGroupDocuments?.map((d) => d.id)),
    [selected?.productDealerGroupDocuments]
  );
  const listProductDocumentTranslationsApi = useApi(
    {
      action: documentTranslationListApi,
      body: { documentType: 'ProductDealerGroupDocument', documentId: productDealerGroupDocIds },
      wait: !productDealerGroupDocIds.length,
    },
    [productDealerGroupDocIds]
  );
  const enhancementDealerGroupDocIds = useMemo(
    () => compact(selected?.productEnhancementDealerGroupDocuments?.map((d) => d.id)),
    [selected?.productEnhancementDealerGroupDocuments]
  );
  const listEnhancementDocumentTranslationsApi = useApi(
    {
      action: documentTranslationListApi,
      body: { documentType: 'ProductEnhancementDealerGroupDocument', documentId: enhancementDealerGroupDocIds },
      wait: !enhancementDealerGroupDocIds.length,
    },
    [enhancementDealerGroupDocIds]
  );
  return (
    <ModalForm<DealerGroupForm>
      subcomponents={ThemeModalFormDefault}
      dependencyArray={[
        selected,
        listProductDocumentTranslationsApi?.payload,
        listEnhancementDocumentTranslationsApi?.payload,
      ]}
      title={lang.group}
      default={
        selected
          ? {
              ...selected.toJSON(),
              logo: selected.uiSettingsFile
                ? {
                    id: selected.uiSettingsFile.id,
                    dataUrl: selected.uiSettingsFile.url,
                    name: selected.uiSettingsFile.name,
                    type: selected.uiSettingsFile.contentType as Utils.File.MimeType,
                  }
                : null,
              productDealerGroupDocuments: selected?.productDealerGroupDocuments?.map((it) => ({
                ...it,
                translatedDocuments: listProductDocumentTranslationsApi.payload?.data.filter(
                  (t) => t.documentId === it.id
                ),
              })),
              productEnhancementDealerGroupDocuments: selected?.productEnhancementDealerGroupDocuments?.map((it) => ({
                ...it,
                translatedDocuments: listEnhancementDocumentTranslationsApi.payload?.data.filter(
                  (t) => t.documentId === it.id
                ),
              })),
            }
          : {
              distributorId: user?.distributorId,
            }
      }
      onCancel={pageRouter.updateParams}
      onSubmit={async ({
        dealers,
        logo,
        productDealerGroupDocuments,
        productEnhancementDealerGroupDocuments,
        claimTypeDealerGroupAutomationRules,
        dealerGroupClaimTypes,
        ...data
      }) => {
        const response = await dealerGroupSaveApi(data);

        if (response.status === HttpStatus.ok) {
          let totalFiles = 0;
          let uploadedFiles = 0;
          let deletedFiles = 0;
          let totalDeletedFiles = 0;
          for (const dealer of dealers ?? []) {
            const selected = dealersPayload?.data?.find((d) => d.id === dealer.id);
            await dealerSaveApi(selected);
          }
          for (const productDealerGroupDoc of productDealerGroupDocuments || []) {
            let documentId: null | number = productDealerGroupDoc.id;
            if (productDealerGroupDoc.isDeleted && productDealerGroupDoc.id) {
              const docDeleteReponse = await dealerGroupDeleteProductDocumentApi({
                productDealerGroupDocumentId: productDealerGroupDoc.id,
              });
              if (docDeleteReponse.status === HttpStatus.ok) {
                deletedFiles += 1;
              }
              totalDeletedFiles += 1;
            } else if (productDealerGroupDoc.formFile) {
              const docUploadResponse = await dealerGroupUploadProductDocumentApi(
                {
                  id: response.payload || data.id,
                  productId: productDealerGroupDoc.productDocument.productId,
                  productDocumentId: productDealerGroupDoc.productDocumentId,
                },
                { formFile: productDealerGroupDoc.formFile.original }
              );
              documentId = docUploadResponse?.payload?.documentId;
              if (docUploadResponse.status === HttpStatus.ok) {
                uploadedFiles += 1;
              }
              totalFiles += 1;
            }
            for (const translatedDoc of productDealerGroupDoc.translatedDocuments || []) {
              if ((productDealerGroupDoc.isDeleted || translatedDoc.isDeleted) && translatedDoc.id) {
                const docDeleteReponse = await documentTranslationDeleteApi({
                  id: translatedDoc.id,
                });
                if (docDeleteReponse.status === HttpStatus.ok) {
                  deletedFiles += 1;
                }
                totalDeletedFiles += 1;
              } else if (translatedDoc.formFile && !translatedDoc.id) {
                const docUploadResponse = await documentTranslationSaveApi({ ...translatedDoc, documentId });
                if (docUploadResponse.status === HttpStatus.ok) {
                  uploadedFiles += 1;
                }
                totalFiles += 1;
              }
            }
          }
          for (const productEnhancementDealerGroupDoc of productEnhancementDealerGroupDocuments || []) {
            let documentId: null | number = productEnhancementDealerGroupDoc.id;
            if (productEnhancementDealerGroupDoc.isDeleted && productEnhancementDealerGroupDoc.id) {
              const docDeleteReponse = await dealerGroupDeleteProductEnhancementDocumentApi({
                productEnhancementDealerGroupDocumentId: productEnhancementDealerGroupDoc.id,
              });
              if (docDeleteReponse.status === HttpStatus.ok) {
                deletedFiles += 1;
              }
              totalDeletedFiles += 1;
            } else if (productEnhancementDealerGroupDoc.formFile) {
              const docUploadResponse = await dealerGroupUploadProductEnhancementDocumentApi(
                {
                  id: response.payload || data.id,
                  productEnhancementId:
                    productEnhancementDealerGroupDoc.productEnhancementDocument.productEnhancementId,
                  productEnhancementDocumentId: productEnhancementDealerGroupDoc.productEnhancementDocumentId,
                },
                { formFile: productEnhancementDealerGroupDoc.formFile.original }
              );
              documentId = docUploadResponse?.payload?.documentId;
              if (docUploadResponse.status === HttpStatus.ok) {
                uploadedFiles += 1;
              }
              totalFiles += 1;
            }
            for (const translatedDoc of productEnhancementDealerGroupDoc.translatedDocuments || []) {
              if ((productEnhancementDealerGroupDoc.isDeleted || translatedDoc.isDeleted) && translatedDoc.id) {
                const docDeleteReponse = await documentTranslationDeleteApi({
                  id: translatedDoc.id,
                });
                if (docDeleteReponse.status === HttpStatus.ok) {
                  deletedFiles += 1;
                }
                totalDeletedFiles += 1;
              } else if (translatedDoc.formFile && !translatedDoc.id) {
                const docUploadResponse = await documentTranslationSaveApi({ ...translatedDoc, documentId });
                if (docUploadResponse.status === HttpStatus.ok) {
                  uploadedFiles += 1;
                }
                totalFiles += 1;
              }
            }
          }
          if (logo?.id !== selected?.uiSettingsFile?.id) {
            const logoUploadResponse = await dealerGroupDeleteUiFileApi(response.payload || data.id);
            if (logoUploadResponse.status === HttpStatus.ok) {
              deletedFiles += 1;
            }
            totalDeletedFiles += 1;
          }
          if (logo && !logo?.id) {
            const logoUploadResponse = await dealerGroupUploadUiFileApi(response.payload || data.id, {
              formFile: logo.original,
            });
            if (logoUploadResponse.status === HttpStatus.ok) {
              uploadedFiles += 1;
            }
            totalFiles += 1;
          }
          await saveDealerGroupClaimTypes(dealerGroupClaimTypes, response.payload);
          await saveDealerGroupAutomationRules(
            claimTypeDealerGroupAutomationRules,
            dealerGroupClaimTypes,
            response.payload
          );
          listApi.execute((body) => body);
          if (uploadedFiles !== totalFiles || deletedFiles !== totalDeletedFiles) {
            addLog({
              warning: combineStrings(
                ' ',
                lang.formSuccessAndFilesFailedMessage,
                totalFiles > 0 ? `${uploadedFiles}/${totalFiles} ${lang.filesUploaded}` : null,
                totalDeletedFiles > 0 ? `${deletedFiles}/${totalDeletedFiles} ${lang.filesGotDeleted}!` : null
              ),
            });
          } else {
            addLog({ success: lang.saveSuccess });
          }
        } else {
          addLog({ error: response.message });
        }
        listApi.execute((body) => body);
        pageRouter.updateParams({});
      }}
      validation={(data, errors) => {
        if (!data.name) errors.name = lang.mustNotBeEmpty;
        if (!data.distributorId) errors.distributorId = lang.mustNotBeEmpty;
        if (data.claimTypeDealerGroupAutomationRules?.some((rule) => !rule.isDeleted && !rule.effects?.length)) {
          set(errors, 'claimTypeDealerGroupAutomationRules._objectError', lang.required);
        }
      }}
      tabs={[
        ...(isDistributor || isDealerOrDistributor || isAdministrator
          ? [{ id: 'info', icon: 'fas-info-circle', text: lang.info }]
          : []),
        ...(isDistributor || isDealerOrDistributor || isAdministrator
          ? [{ id: 'productDocuments', icon: 'fas-file', text: lang.productDocs }]
          : []),
        ...(isDistributor || isDealerOrDistributor || isAdministrator
          ? [{ id: 'productEnhancementDocuments', icon: 'fas-file-alt', text: lang.enhDocs }]
          : []),
        ...(isDistributor || isDealerOrDistributor || isAdministrator
          ? [{ id: 'automationRules', icon: 'fas-file', text: lang.automationRules }]
          : []),
        ...(isDistributor || isDealerOrDistributor || isAdministrator
          ? [
              {
                id: 'contractConsentProductAggregates',
                icon: 'fas-database',
                text: lang.consentProductAgg,
              },
            ]
          : []),
        ...(isDistributor || isDealerOrDistributor || isAdministrator
          ? [{ id: 'uiSettings', icon: 'fas-paint-roller', text: lang.uiSettings }]
          : []),
      ]}
      render={({ activeTab }) =>
        activeTab?.id === 'info' || !activeTab?.id ? (
          <DealerGroupGeneralForm />
        ) : activeTab?.id === 'uiSettings' ? (
          <DealerGroupUiSettingsForm />
        ) : activeTab?.id === 'group' ? (
          <DealerGroupGeneralForm />
        ) : activeTab?.id === 'productDocuments' ? (
          <DealerGroupProductDocumentForm />
        ) : activeTab?.id === 'productEnhancementDocuments' ? (
          <DealerGroupProductEnhancementDocumentForm />
        ) : activeTab?.id === 'contractConsentProductAggregates' ? (
          <DealerGroupContractConsentProductAggregate />
        ) : activeTab?.id === 'automationRules' ? (
          <DealerGroupClaimTypeAutomationRules />
        ) : null
      }
    />
  );
}
